import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Crown, Star, MapPin, MessageSquare, Instagram, Mail, Phone,
  Calendar, Clock, ChevronLeft, ChevronRight, Check, X,
  FileText, CreditCard, Loader2, ExternalLink, Shield,
  Image as ImageIcon, Briefcase, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getPalette, colorPalettes } from '../data/portfolioMock';

const ProPublico = () => {
  const { slug } = useParams();
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('portfolio');
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(null); // 'quote', 'booking', 'payment'
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadProfessional();
  }, [slug]);

  const loadProfessional = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pro/professionals/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setProfessional(data);
      }
    } catch (e) {
      console.error('Error loading professional:', e);
    }
    setLoading(false);
  };

  const getColors = () => {
    if (!professional) return colorPalettes[0];
    return getPalette(professional.colorPalette) || colorPalettes[0];
  };

  const colors = professional ? getColors() : colorPalettes[0];

  const handleWhatsApp = (message = '') => {
    const phone = professional?.phone?.replace(/\D/g, '');
    const defaultMsg = message || `Olá! Vi seu perfil no AtivaJá e gostaria de mais informações.`;
    window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(defaultMsg)}`, '_blank');
  };

  const handleQuoteSubmit = async () => {
    if (!formData.name || !formData.phone) return;
    setSubmitting(true);
    
    try {
      await fetch(`${API_URL}/api/pro/professionals/${slug}/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          service: selectedService,
          created_at: new Date().toISOString()
        })
      });
      setSuccess(true);
      
      // Notificar via WhatsApp
      const msg = `🔔 *Novo orçamento solicitado!*\n\nCliente: ${formData.name}\nTelefone: ${formData.phone}\nServiço: ${selectedService?.name || 'Não especificado'}\n\nResponda o mais rápido possível!`;
      // Abrir WhatsApp para o profissional responder
    } catch (e) {
      console.error('Error submitting quote:', e);
    }
    setSubmitting(false);
  };

  const handleBookingSubmit = async () => {
    if (!formData.name || !formData.phone || !selectedDate || !selectedTime) return;
    setSubmitting(true);
    
    try {
      await fetch(`${API_URL}/api/pro/professionals/${slug}/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          service: selectedService,
          date: selectedDate,
          time: selectedTime,
          status: 'pending'
        })
      });
      
      if (professional.requireDeposit) {
        setShowModal('payment');
      } else {
        setSuccess(true);
      }
    } catch (e) {
      console.error('Error booking:', e);
    }
    setSubmitting(false);
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before first day
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isWorkingDay = (date) => {
    if (!date || !professional?.workingDays) return false;
    const dayNames = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'];
    return professional.workingDays[dayNames[date.getDay()]];
  };

  const getAvailableTimes = () => {
    if (!professional?.workingHours) return [];
    const times = [];
    const [startH] = professional.workingHours.start.split(':').map(Number);
    const [endH] = professional.workingHours.end.split(':').map(Number);
    const duration = professional.slotDuration || 60;
    
    for (let h = startH; h < endH; h++) {
      times.push(`${h.toString().padStart(2, '0')}:00`);
      if (duration <= 30 && h < endH - 1) {
        times.push(`${h.toString().padStart(2, '0')}:30`);
      }
    }
    return times;
  };

  const avgRating = professional?.testimonials?.length > 0
    ? (professional.testimonials.reduce((a, t) => a + t.rating, 0) / professional.testimonials.length).toFixed(1)
    : '5.0';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-50">
        <Crown size={48} className="text-slate-300 mb-4" />
        <h1 className="text-xl font-bold text-slate-900 mb-2">Profissional não encontrado</h1>
        <p className="text-slate-600">Este perfil Pro não existe ou foi desativado.</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${professional.darkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
      {/* Header/Cover */}
      <div className="relative h-48 sm:h-56">
        {professional.coverPhoto ? (
          <img src={professional.coverPhoto} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full bg-gradient-to-r ${colors.gradient}`} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20" />
        
        {/* Badge Pro */}
        <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full text-white text-sm font-medium shadow-lg">
          <Crown size={14} />
          Pro Verificado
        </div>
      </div>

      {/* Profile Card */}
      <div className="max-w-4xl mx-auto px-4 -mt-16 relative z-10">
        <div className={`${professional.darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white'} rounded-2xl shadow-xl p-6 border`}>
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {professional.photo ? (
                <img 
                  src={professional.photo} 
                  alt={professional.name}
                  className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover border-4 border-white shadow-lg mx-auto sm:mx-0"
                />
              ) : (
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mx-auto sm:mx-0">
                  <span className="text-4xl text-white font-bold">{professional.name?.[0]}</span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className={`text-2xl font-bold ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                {professional.business || professional.name}
              </h1>
              
              {professional.tagline && (
                <p className={`${professional.darkMode ? 'text-slate-400' : 'text-slate-600'} mt-1`}>
                  {professional.tagline}
                </p>
              )}

              <div className={`flex flex-wrap justify-center sm:justify-start gap-4 mt-3 text-sm ${professional.darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {professional.city && (
                  <span className="flex items-center gap-1">
                    <MapPin size={14} />
                    {professional.city}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Star size={14} className="text-amber-500 fill-amber-500" />
                  {avgRating} ({professional.testimonials?.length || 0} avaliações)
                </span>
                {professional.yearsExperience && (
                  <span className="flex items-center gap-1">
                    <Award size={14} />
                    {professional.yearsExperience} anos de experiência
                  </span>
                )}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3 mt-4 justify-center sm:justify-start">
                <Button 
                  onClick={() => handleWhatsApp()}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <MessageSquare size={18} />
                  WhatsApp
                </Button>
                <Button 
                  onClick={() => {
                    setSelectedService(null);
                    setShowModal('quote');
                  }}
                  style={{ backgroundColor: colors.primary }}
                  className="hover:opacity-90"
                >
                  <FileText size={18} />
                  Pedir Orçamento
                </Button>
                <Button 
                  onClick={() => {
                    setSelectedService(null);
                    setShowModal('booking');
                  }}
                  variant="outline"
                  className={professional.darkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}
                >
                  <Calendar size={18} />
                  Agendar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 mt-6">
        <div className={`flex gap-1 p-1 rounded-xl ${professional.darkMode ? 'bg-slate-900' : 'bg-white'} shadow`}>
          {[
            { id: 'portfolio', label: 'Portfólio', icon: ImageIcon },
            { id: 'services', label: 'Serviços', icon: Briefcase },
            { id: 'reviews', label: 'Avaliações', icon: Star },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? `bg-gradient-to-r ${colors.gradient} text-white`
                  : professional.darkMode 
                    ? 'text-slate-400 hover:text-white' 
                    : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <tab.icon size={18} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && (
          <div>
            {professional.bio && (
              <div className={`mb-8 p-6 rounded-xl ${professional.darkMode ? 'bg-slate-900' : 'bg-white'} shadow`}>
                <h3 className={`font-semibold mb-2 ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Sobre
                </h3>
                <p className={professional.darkMode ? 'text-slate-400' : 'text-slate-600'}>
                  {professional.bio}
                </p>
              </div>
            )}

            {professional.works?.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {professional.works.map((work, i) => (
                  <div key={i} className={`rounded-xl overflow-hidden ${professional.darkMode ? 'bg-slate-900' : 'bg-white'} shadow`}>
                    {work.images?.[0] && (
                      <img src={work.images[0].url} alt={work.title} className="w-full h-48 object-cover" />
                    )}
                    <div className="p-4">
                      <h4 className={`font-semibold ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {work.title}
                      </h4>
                      <p className={`text-sm ${professional.darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        {work.description}
                      </p>
                      <span className={`inline-block mt-2 text-xs px-2 py-1 rounded-full ${professional.darkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                        {work.category}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-12 ${professional.darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <ImageIcon size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nenhum trabalho cadastrado ainda</p>
              </div>
            )}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-4">
            {professional.services?.length > 0 ? (
              professional.services.map((service, i) => (
                <div 
                  key={i} 
                  className={`flex items-center justify-between p-4 rounded-xl ${professional.darkMode ? 'bg-slate-900' : 'bg-white'} shadow`}
                >
                  <div>
                    <h4 className={`font-semibold ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {service.name}
                    </h4>
                    <p className={`text-sm ${professional.darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <Clock size={14} className="inline mr-1" />
                      {service.duration} min
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold" style={{ color: colors.primary }}>
                      R$ {service.price}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedService(service);
                          setShowModal('quote');
                        }}
                        className={professional.darkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}
                      >
                        Orçamento
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => {
                          setSelectedService(service);
                          setShowModal('booking');
                        }}
                        style={{ backgroundColor: colors.primary }}
                      >
                        Agendar
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={`text-center py-12 ${professional.darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <Briefcase size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nenhum serviço cadastrado ainda</p>
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-4">
            {professional.testimonials?.length > 0 ? (
              professional.testimonials.map((review, i) => (
                <div key={i} className={`p-4 rounded-xl ${professional.darkMode ? 'bg-slate-900' : 'bg-white'} shadow`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-medium">
                      {review.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                          {review.name}
                        </span>
                        <div className="flex">
                          {[...Array(5)].map((_, j) => (
                            <Star 
                              key={j} 
                              size={14} 
                              className={j < review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'} 
                            />
                          ))}
                        </div>
                      </div>
                      <p className={`mt-1 ${professional.darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        {review.text}
                      </p>
                      <span className={`text-xs ${professional.darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        {review.service} • {review.date}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={`text-center py-12 ${professional.darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <Star size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nenhuma avaliação ainda</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {!professional.pro_benefits?.remove_branding && (
        <footer className={`text-center py-4 text-xs ${professional.darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
          Feito com ❤️ no <a href="/" className="underline">AtivaJá</a>
        </footer>
      )}

      {/* Quote Modal */}
      {showModal === 'quote' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${professional.darkMode ? 'bg-slate-900' : 'bg-white'} rounded-2xl max-w-md w-full p-6`}>
            {success ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-green-600" />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Orçamento solicitado!
                </h3>
                <p className={professional.darkMode ? 'text-slate-400' : 'text-slate-600'}>
                  {professional.business} vai entrar em contato em breve.
                </p>
                <Button onClick={() => { setShowModal(null); setSuccess(false); }} className="mt-4">
                  Fechar
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className={`text-xl font-bold ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                    Solicitar Orçamento
                  </h3>
                  <button onClick={() => setShowModal(null)} className={professional.darkMode ? 'text-slate-400' : 'text-slate-500'}>
                    <X size={24} />
                  </button>
                </div>

                {selectedService && (
                  <div className={`mb-4 p-3 rounded-lg ${professional.darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className={`font-medium ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {selectedService.name}
                    </p>
                    <p className="text-sm" style={{ color: colors.primary }}>
                      A partir de R$ {selectedService.price}
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <Label className={professional.darkMode ? 'text-slate-300' : ''}>Seu nome *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`mt-1 ${professional.darkMode ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                    />
                  </div>
                  <div>
                    <Label className={professional.darkMode ? 'text-slate-300' : ''}>WhatsApp *</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(11) 99999-9999"
                      className={`mt-1 ${professional.darkMode ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                    />
                  </div>
                  <div>
                    <Label className={professional.darkMode ? 'text-slate-300' : ''}>Mensagem (opcional)</Label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Descreva o que você precisa..."
                      rows={3}
                      className={`w-full mt-1 p-3 border rounded-lg resize-none ${professional.darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'border-slate-200'}`}
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleQuoteSubmit}
                  disabled={submitting || !formData.name || !formData.phone}
                  className="w-full mt-6"
                  style={{ backgroundColor: colors.primary }}
                >
                  {submitting ? <Loader2 className="animate-spin" /> : 'Enviar solicitação'}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showModal === 'booking' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`${professional.darkMode ? 'bg-slate-900' : 'bg-white'} rounded-2xl max-w-md w-full p-6 my-8`}>
            {success ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-green-600" />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Agendamento confirmado!
                </h3>
                <p className={professional.darkMode ? 'text-slate-400' : 'text-slate-600'}>
                  {selectedDate?.toLocaleDateString('pt-BR')} às {selectedTime}
                </p>
                <Button onClick={() => { setShowModal(null); setSuccess(false); }} className="mt-4">
                  Fechar
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className={`text-xl font-bold ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                    Agendar
                  </h3>
                  <button onClick={() => setShowModal(null)} className={professional.darkMode ? 'text-slate-400' : 'text-slate-500'}>
                    <X size={24} />
                  </button>
                </div>

                {selectedService && (
                  <div className={`mb-4 p-3 rounded-lg ${professional.darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className={`font-medium ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {selectedService.name}
                    </p>
                    <p className="text-sm" style={{ color: colors.primary }}>
                      R$ {selectedService.price} • {selectedService.duration} min
                    </p>
                  </div>
                )}

                {/* Calendar */}
                <div className={`mb-4 p-4 rounded-lg ${professional.darkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}>
                      <ChevronLeft size={20} className={professional.darkMode ? 'text-slate-400' : 'text-slate-600'} />
                    </button>
                    <span className={`font-medium ${professional.darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                    </span>
                    <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}>
                      <ChevronRight size={20} className={professional.darkMode ? 'text-slate-400' : 'text-slate-600'} />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2">
                    {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
                      <span key={i} className={professional.darkMode ? 'text-slate-500' : 'text-slate-400'}>{d}</span>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1">
                    {getDaysInMonth(currentMonth).map((day, i) => (
                      <button
                        key={i}
                        onClick={() => day && isWorkingDay(day) && day >= new Date() && setSelectedDate(day)}
                        disabled={!day || !isWorkingDay(day) || day < new Date()}
                        className={`p-2 rounded-lg text-sm ${
                          !day ? '' :
                          selectedDate?.toDateString() === day.toDateString()
                            ? `bg-gradient-to-r ${colors.gradient} text-white`
                            : !isWorkingDay(day) || day < new Date()
                              ? professional.darkMode ? 'text-slate-700' : 'text-slate-300'
                              : professional.darkMode ? 'text-white hover:bg-slate-700' : 'hover:bg-slate-200'
                        }`}
                      >
                        {day?.getDate()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Times */}
                {selectedDate && (
                  <div className="mb-4">
                    <Label className={`mb-2 block ${professional.darkMode ? 'text-slate-300' : ''}`}>Horário</Label>
                    <div className="flex flex-wrap gap-2">
                      {getAvailableTimes().map((time) => (
                        <button
                          key={time}
                          onClick={() => setSelectedTime(time)}
                          className={`px-3 py-2 rounded-lg text-sm ${
                            selectedTime === time
                              ? `bg-gradient-to-r ${colors.gradient} text-white`
                              : professional.darkMode 
                                ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' 
                                : 'bg-slate-100 hover:bg-slate-200'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <Label className={professional.darkMode ? 'text-slate-300' : ''}>Seu nome *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`mt-1 ${professional.darkMode ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                    />
                  </div>
                  <div>
                    <Label className={professional.darkMode ? 'text-slate-300' : ''}>WhatsApp *</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className={`mt-1 ${professional.darkMode ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleBookingSubmit}
                  disabled={submitting || !formData.name || !formData.phone || !selectedDate || !selectedTime}
                  className="w-full mt-6"
                  style={{ backgroundColor: colors.primary }}
                >
                  {submitting ? <Loader2 className="animate-spin" /> : 'Confirmar agendamento'}
                </Button>

                {professional.requireDeposit && (
                  <p className={`text-xs text-center mt-2 ${professional.darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Será necessário pagar {professional.depositPercent}% de sinal
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProPublico;
